import { getLocalizedText } from './i18n';

const SCORM_SESSION_KEY = 'student_scorm_session';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Téléchargement compatible navigateur, WebView in-app et iframe SCORM mobile.
 *
 * Sur desktop / navigateur classique : ancre `download` + blob URL.
 * Sur mobile SCORM (iframe / WebView) : Web Share, puis ouverture dans un nouvel onglet
 * (le viewer natif du LMS propose enregistrer / partager).
 */

/** Détecte une WebView in-app (et non un vrai navigateur). Heuristique basée sur le user-agent. */
export function isInAppWebView(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';

  const isAndroid = /Android/.test(ua);
  const isAndroidWebView = isAndroid && /\bwv\b/.test(ua);

  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isSafari = /Safari/.test(ua);
  const isStandalone = (navigator as any).standalone === true;
  const isIOSWebView = isIOS && !isSafari && !isStandalone;

  return isAndroidWebView || isIOSWebView;
}

function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
}

function hasScormSession(): boolean {
  try {
    const raw = localStorage.getItem(SCORM_SESSION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { token?: string; lmsUserId?: string };
    return Boolean(parsed.token && parsed.lmsUserId);
  } catch {
    return false;
  }
}

function isInIframe(): boolean {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Environnement où blob + ancre `download` échoue souvent silencieusement
 * (iframe SCORM mobile, WebView LMS, etc.).
 */
export function isRestrictedDownloadEnvironment(): boolean {
  if (!isMobileDevice()) {
    return isInAppWebView();
  }
  return isInAppWebView() || isInIframe() || hasScormSession();
}

export function getStudentAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('student_token');
}

/** URL absolue avec jeton en query pour ouverture dans un nouvel onglet (GET authentifié). */
export function buildAuthenticatedDownloadUrl(url: string): string {
  const token = getStudentAuthToken();
  const absolute = url.startsWith('http://') || url.startsWith('https://')
    ? url
    : new URL(url, API_BASE_URL).toString();
  if (!token) return absolute;
  const parsed = new URL(absolute);
  parsed.searchParams.set('access_token', token);
  return parsed.toString();
}

/** Résout une URL média relative (/uploads, CDN, etc.) en URL absolue. */
export function resolveMediaFileUrl(url: string): string {
  const baseUrl = API_BASE_URL.replace(/\/api\/?$/, '');
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    return url.startsWith('/uploads/') ? `${API_BASE_URL}${url}` : `${baseUrl}${url}`;
  }
  return `${API_BASE_URL}/uploads/${url}`;
}

function extractFileExtension(source: string): string {
  const basename = (source.split('/').pop() || source).split('?')[0].trim();
  const lastDot = basename.lastIndexOf('.');
  if (lastDot <= 0) return '';
  const ext = basename.slice(lastDot).toLowerCase();
  return /^\.[a-z0-9]{1,8}$/.test(ext) ? ext : '';
}

function sanitizeDownloadFilename(name: string): string {
  return name.replace(/[/\\:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();
}

/** Nom de fichier téléchargé pour un module « file » : titre affiché + extension réelle du fichier. */
export function buildModuleFileDownloadName(
  content: {
    title?: unknown;
    fileName?: unknown;
    fileUrl?: unknown;
    url?: unknown;
  } | null | undefined,
  fallback = 'file'
): string {
  const title = getLocalizedText(content?.title).trim();
  const storedName = getLocalizedText(content?.fileName).trim();
  const fileUrl = getLocalizedText(content?.fileUrl || content?.url).trim();

  const extension = extractFileExtension(storedName) || extractFileExtension(fileUrl);
  let base = sanitizeDownloadFilename(title || storedName || fallback);
  if (!base) base = fallback;

  if (extension && !base.toLowerCase().endsWith(extension)) {
    return `${base}${extension}`;
  }
  return base;
}

function canShareFiles(files: File[]): boolean {
  const nav = navigator as any;
  return (
    typeof nav !== 'undefined' &&
    typeof nav.share === 'function' &&
    typeof nav.canShare === 'function' &&
    nav.canShare({ files })
  );
}

/** Ouvre une URL dans un nouvel onglet (viewer natif / WebView LMS). */
export function openDownloadUrl(url: string, options?: { authenticate?: boolean }): void {
  const openUrl = options?.authenticate ? buildAuthenticatedDownloadUrl(url) : url;
  openUrlInNewTab(openUrl);
}

function openUrlInNewTab(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function openBlobInNewTab(blob: Blob, mimeType: string): void {
  const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
  openUrlInNewTab(url);
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

/** Téléchargement classique (navigateurs) : ancre `download` + blob URL. */
function anchorDownload(blob: Blob, fileName: string, mimeType: string): void {
  const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

async function tryWebShare(blob: Blob, fileName: string, mimeType: string): Promise<boolean> {
  try {
    const file = new File([blob], fileName, { type: mimeType });
    if (!canShareFiles([file])) return false;
    await (navigator as any).share({ files: [file], title: fileName });
    return true;
  } catch (err: any) {
    if (err && err.name === 'AbortError') return true;
    return false;
  }
}

/**
 * Télécharge une ressource HTTP (module fichier, certificat, etc.).
 * En contexte SCORM mobile : ouverture directe de l'URL (auth via query si nécessaire).
 */
export async function downloadResourceFromUrl(
  rawUrl: string,
  fileName = 'file',
  options?: { requiresAuth?: boolean }
): Promise<boolean> {
  const fileUrl = resolveMediaFileUrl(rawUrl);
  const requiresAuth = options?.requiresAuth ?? /\/api\//.test(fileUrl);
  const downloadUrl = new URL(fileUrl);
  if (fileName && fileName !== 'file') {
    downloadUrl.searchParams.set('filename', fileName);
  }
  const requestUrl = downloadUrl.toString();

  if (isRestrictedDownloadEnvironment()) {
    const openUrl = requiresAuth ? buildAuthenticatedDownloadUrl(requestUrl) : requestUrl;
    openUrlInNewTab(openUrl);
    return true;
  }

  const headers: Record<string, string> = {};
  const token = getStudentAuthToken();
  if (requiresAuth && token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(requestUrl, { method: 'GET', headers });
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status}`);
    }
    const blob = await response.blob();
    await saveBlobAsFile(blob, fileName, blob.type || undefined);
    return true;
  } catch (error) {
    // Presigned S3 302 is cross-origin: without bucket CORS, fetch cannot read the body.
    // Navigating to the API URL lets the browser follow the redirect (no CORS).
    console.warn('Blob download failed, opening URL instead:', error);
    openUrlInNewTab(requiresAuth ? buildAuthenticatedDownloadUrl(requestUrl) : requestUrl);
    return true;
  }
}

/**
 * Enregistre un blob en tant que fichier, de façon compatible WebView / SCORM mobile.
 */
export async function saveBlobAsFile(
  blob: Blob,
  fileName: string,
  mimeType?: string
): Promise<boolean> {
  const type = mimeType || blob.type || 'application/octet-stream';

  if (isRestrictedDownloadEnvironment()) {
    if (await tryWebShare(blob, fileName, type)) {
      return true;
    }
    openBlobInNewTab(blob, type);
    return true;
  }

  anchorDownload(blob, fileName, type);
  return true;
}
